<template>
  <a-row
    type="flex"
    justify="center"
    align="middle"
    style="min-height: 100vh"
    class="ml-3 mr-3"
  >
    <a-spin v-if="state.loading" size="large" />
    <a-col v-if="!state.loading" :sm="20" :md="14" :lg="10">
      <div style="margin-top: 16px">
        <a-steps direction="horizontal" :current="1">
          <a-step
            v-for="(step, stepIndex) in steps"
            :key="stepIndex"
            :title="step.title"
          />
        </a-steps>
      </div>
      <a-typography-title :level="4" style="margin-top: 16px">
        Selecciona las coberturas
      </a-typography-title>
      <a-typography-paragraph>
        Selecciona al menos una cobertura por cada carga.
      </a-typography-paragraph>
      <a-card
        style="margin-top: 16px"
        title="Asignación de coberturas por carga"
      >
        <a-alert
          type="info"
          show-icon
          message="Debes seleccionar al menos una cobertura por cada carga."
          class="mb-3"
        />
        <a-spin :spinning="state.loadingCoverageMatrix">
          <a-empty
            v-if="state.coverageGroups.length === 0"
            description="No hay coberturas disponibles para asignar."
          />
          <a-card
            v-for="group in state.coverageGroups"
            :key="group.key"
            size="small"
            style="margin-bottom: 12px"
          >
            <template #title>
              Póliza #{{ group.policyNumber }} · Grupo {{ group.groupName }}
            </template>
            <a-row
              v-for="coverage in group.coverages"
              :key="coverage.id"
              type="flex"
              align="middle"
              :gutter="8"
              style="padding: 8px 0; border-bottom: 1px solid #f0f0f0"
            >
              <a-col :xs="24" :md="8">
                <a-typography-text>
                  {{ coverage.name }}
                  <a-tag v-if="coverage.dependentRequired" color="blue">
                    Requerida
                  </a-tag>
                </a-typography-text>
              </a-col>
              <a-col
                v-for="dependent in state.dependents"
                :key="dependent.id"
                :xs="12"
                :md="
                  Math.max(
                    4,
                    Math.floor(16 / Math.max(state.dependents.length, 1))
                  )
                "
              >
                <a-checkbox
                  :checked="isCoverageSelected(dependent.id, coverage.id)"
                  :disabled="
                    state.loadingCoverageMatrix || coverage.dependentRequired
                  "
                  @change="
                    (event) =>
                      toggleCoverage(
                        dependent.id,
                        coverage.id,
                        event.target.checked
                      )
                  "
                >
                  {{ getDependentLabel(dependent) }}
                </a-checkbox>
              </a-col>
            </a-row>
          </a-card>
        </a-spin>
      </a-card>
      <a-row
        style="margin-top: 18px; margin-bottom: 18px"
        :gutter="16"
        type="flex"
        justify="space-between"
      >
        <a-col :span="8">
          <a-button block size="large">
            <router-link
              :to="{
                name: 'DependentsIncorporationNoDeliverableFormStep1',
                params: { token: route.params.token }
              }"
            >
              Atrás
            </router-link>
          </a-button>
        </a-col>
        <a-col :span="8">
          <a-button
            :loading="state.loadingSendToBroker"
            block
            type="primary"
            size="large"
            @click="submitForm"
          >
            Finalizar
          </a-button>
        </a-col>
      </a-row>
    </a-col>
  </a-row>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { reactive, ref, onMounted } from 'vue';
import {
  FETCH_DEPENDENTS_INCORPORATION_FORM,
  FETCH_TICKET_DEPENDENT_INCORPORATIONS,
  FETCH_TICKET_AVAILABLE_COVERAGES,
  FETCH_TICKET_DEPENDENT_INCORPORATION_COVERAGES,
  PATCH_TICKET_STATUS,
  PUT_TICKET_DEPENDENT_INCORPORATION_COVERAGES
} from '@/store/types';
import notification from '@/utils/notifications';

const route = useRoute();
const router = useRouter();
const store = useStore();

const steps = [
  {
    title: 'Ingresa tus cargas'
  },
  {
    title: 'Selecciona coberturas'
  }
];

const state = reactive({
  loading: true,
  form: {},
  ticket: {},
  dependents: [],
  coverageGroups: [],
  coverageSelections: {},
  loadingCoverageMatrix: false,
  loadingSendToBroker: false
});

const ticketId = ref();

const getArrayLength = (value) => (Array.isArray(value) ? value.length : 0);

const getCoverageId = (coverage) => {
  const id =
    coverage?.employeeCoverageId ?? coverage?.operationalId ?? coverage?.id;
  const numericId = Number(id);
  return Number.isInteger(numericId) && numericId > 0 ? numericId : null;
};

const hasAllowDependentsFlag = (coverage) => {
  const flag = [
    coverage?.allowDependents,
    coverage?.coverage?.allowDependents
  ].find((value) => value !== undefined && value !== null);

  return flag === undefined || flag === true || flag === 1 || flag === 'true';
};

const getDependentLabel = (dependent) =>
  [
    dependent?.firstName,
    dependent?.secondName,
    dependent?.firstSurname,
    dependent?.secondSurname
  ]
    .filter(Boolean)
    .join(' ') ||
  dependent?.fullname ||
  dependent?.rut ||
  'Carga';

const buildCoverageGroups = (coverages) => {
  const groups = new Map();

  for (const coverage of coverages) {
    if (!hasAllowDependentsFlag(coverage)) continue;

    const id = getCoverageId(coverage);
    if (!id) continue;

    const policy = coverage?.policy || {};
    const groupCompany = coverage?.groupCompany || {};
    const policyId = policy.id ?? policy.numberPolicy ?? '-';
    const groupId = groupCompany.id ?? groupCompany.name ?? '-';
    const key = `${policyId}-${groupId}`;

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        policyNumber: policy.numberPolicy || policy.id || '-',
        groupName: groupCompany.name || groupCompany.id || '-',
        coverages: []
      });
    }

    groups.get(key).coverages.push({
      id,
      name: coverage?.coverage?.name || 'Cobertura',
      dependentRequired: Boolean(coverage?.coverage?.dependentRequired)
    });
  }

  return Array.from(groups.values());
};

const isCoverageSelected = (dependentId, coverageId) =>
  (state.coverageSelections[dependentId] || []).includes(coverageId);

const toggleCoverage = (dependentId, coverageId, checked) => {
  const current = state.coverageSelections[dependentId] || [];
  state.coverageSelections[dependentId] = checked
    ? current.includes(coverageId)
      ? current
      : [...current, coverageId]
    : current.filter((id) => id !== coverageId);
};

const loadCoverageMatrix = async () => {
  state.loadingCoverageMatrix = true;
  try {
    const [dependents, availableCoverages] = await Promise.all([
      store.dispatch(FETCH_TICKET_DEPENDENT_INCORPORATIONS, {
        ticketId: ticketId.value,
        token: route.params.token
      }),
      store.dispatch(FETCH_TICKET_AVAILABLE_COVERAGES, {
        ticketId: ticketId.value,
        token: route.params.token
      })
    ]);

    state.dependents = Array.isArray(dependents) ? dependents : [];
    state.coverageGroups = buildCoverageGroups(
      Array.isArray(availableCoverages) ? availableCoverages : []
    );
    const availableCoverageIds = new Set(
      state.coverageGroups.flatMap((group) =>
        group.coverages.map((coverage) => coverage.id)
      )
    );

    const requiredCoverageIds = new Set(
      state.coverageGroups.flatMap((group) =>
        group.coverages
          .filter((coverage) => coverage.dependentRequired)
          .map((coverage) => coverage.id)
      )
    );

    const selectionEntries = await Promise.all(
      state.dependents.map(async (dependent) => {
        const response = await store.dispatch(
          FETCH_TICKET_DEPENDENT_INCORPORATION_COVERAGES,
          {
            ticketId: ticketId.value,
            dependentIncorporationId: dependent.id,
            token: route.params.token
          }
        );
        const persistedCoverageIds =
          response?.employeeCoverageIncorporationIds ??
          response?.employeeCoverageIds;
        const selectedCoverageIds = Array.isArray(persistedCoverageIds)
          ? persistedCoverageIds
              .map(Number)
              .filter((id) => availableCoverageIds.has(id))
          : [];

        return [
          dependent.id,
          [...new Set([...selectedCoverageIds, ...requiredCoverageIds])]
        ];
      })
    );

    state.coverageSelections = Object.fromEntries(selectionEntries);
  } finally {
    state.loadingCoverageMatrix = false;
  }
};

const persistCoverageMatrix = async () => {
  for (const dependent of state.dependents) {
    const selectedCoverageIds = state.coverageSelections[dependent.id] || [];
    await store.dispatch(PUT_TICKET_DEPENDENT_INCORPORATION_COVERAGES, {
      ticketId: ticketId.value,
      dependentIncorporationId: dependent.id,
      token: route.params.token,
      payload: {
        coverageIds: selectedCoverageIds
      }
    });
  }
};

const validateDependentsBeforeSendToBroker = () => {
  if (!Array.isArray(state.dependents) || state.dependents.length === 0) {
    notification('error', 'Error', 'Debe incorporar al menos una carga.');
    return false;
  }

  const missingSelections = state.dependents.some(
    (dependent) =>
      getArrayLength(state.coverageSelections[dependent.id] || []) === 0
  );
  if (missingSelections) {
    notification(
      'error',
      'Error',
      'Todas las cargas deben tener al menos una cobertura seleccionada.'
    );
    return false;
  }

  return true;
};

const submitForm = async () => {
  state.loadingSendToBroker = true;

  const isDependentsCoverageValid = validateDependentsBeforeSendToBroker();
  if (!isDependentsCoverageValid) {
    state.loadingSendToBroker = false;
    return;
  }

  try {
    await persistCoverageMatrix();
    await store.dispatch(PATCH_TICKET_STATUS, {
      ticketId: ticketId.value,
      token: route.params.token,
      status: 7
    });
    router.push({
      name: 'DependentsIncorporationNoDeliverableFormSuccess',
      params: { token: route.params.token }
    });
  } catch (error) {
    const status = error?.response?.status;
    const backendMessage = error?.response?.data?.message;
    if (
      (status === 400 || status === 401 || status === 409) &&
      backendMessage
    ) {
      notification('error', 'Error', backendMessage);
      state.loadingSendToBroker = false;
      return;
    }
    notification(
      'error',
      'Error',
      'Ocurrió un error al finalizar el formulario de incorporación de cargas. Intentalo más tarde.'
    );
  }
  state.loadingSendToBroker = false;
};

onMounted(async () => {
  try {
    const token = route.params.token;
    const decodedToken = JSON.parse(atob(token.split('.')[1]));
    ticketId.value = decodedToken.tid;

    const form = await store.dispatch(
      FETCH_DEPENDENTS_INCORPORATION_FORM,
      route.params.token
    );
    state.form = form;

    if (form.state !== 'Creado') {
      router.push({
        name: 'DependentsIncorporationNoDeliverableFormError'
      });
    } else if (form.isTokenExpired) {
      router.push({
        name: 'DependentsIncorporationNoDeliverableFormExpired'
      });
    }

    await loadCoverageMatrix();

    state.loading = false;
  } catch (error) {
    console.log(error);
    router.push({
      name: 'DependentsIncorporationNoDeliverableFormError'
    });
  }
});
</script>
